import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MailX, MailCheck, Loader2 } from "lucide-react";

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | done | resubscribed | error
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("error"); return; }
    api.get("/email/unsubscribe/info", { params: { token } })
      .then(({ data }) => { setInfo(data); setState(data.opted_out ? "done" : "ready"); })
      .catch(() => setState("error"));
  }, [token]);

  const act = async (path, next) => {
    setBusy(true);
    try {
      await api.post(`/email/${path}`, { token });
      setState(next);
    } catch { setState("error"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-6">
      <div className="card-surface max-w-md w-full p-8 text-center" data-testid="unsubscribe-page">
        {state === "loading" && <Loader2 className="animate-spin mx-auto text-zinc-500" />}
        {state === "error" && (
          <>
            <MailX className="mx-auto mb-4 text-rose-500" size={32} />
            <h1 className="font-display text-2xl mb-2">Link not valid</h1>
            <p className="text-sm text-zinc-500">This unsubscribe link is invalid or incomplete. Use the link from the latest email.</p>
          </>
        )}
        {state === "ready" && (
          <>
            <MailX className="mx-auto mb-4 text-rose-500" size={32} />
            <h1 className="font-display text-2xl mb-2">Unsubscribe from marketing emails?</h1>
            <p className="text-sm text-zinc-500 mb-6">
              {info?.email} will stop receiving My Panda X announcements and promotions.
              Account emails (password resets, security notices) will still arrive.
            </p>
            <Button data-testid="unsubscribe-confirm-btn" disabled={busy} onClick={() => act("unsubscribe", "done")} className="btn-crimson rounded-full h-11 px-6 w-full">
              {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <MailX size={14} className="mr-2" />} Unsubscribe
            </Button>
          </>
        )}
        {state === "done" && (
          <>
            <MailCheck className="mx-auto mb-4 text-emerald-400" size={32} />
            <h1 className="font-display text-2xl mb-2">You're unsubscribed</h1>
            <p className="text-sm text-zinc-500 mb-6">No more marketing emails. Changed your mind?</p>
            <Button data-testid="resubscribe-btn" disabled={busy} onClick={() => act("resubscribe", "resubscribed")} variant="ghost" className="rounded-full border border-white/10 h-10 px-5">
              Re-subscribe
            </Button>
          </>
        )}
        {state === "resubscribed" && (
          <>
            <MailCheck className="mx-auto mb-4 text-emerald-400" size={32} />
            <h1 className="font-display text-2xl mb-2">Welcome back</h1>
            <p className="text-sm text-zinc-500">You'll receive My Panda X announcements again.</p>
          </>
        )}
        <p className="text-[11px] text-zinc-600 mt-8">
          MyPandaX LLC · 108 Walltown St, Lexington, NC 27292 · <Link to="/" className="underline">mypandax.com</Link>
        </p>
      </div>
    </div>
  );
}
